import type { ComponentType } from 'react'
import { lazy } from 'react'
import type { SEOConfig } from '../components/common/SEO'

// Lazy load pages for code splitting
const HomePage = lazy(() => import('../pages/home/homePage'))
const CampaignPage = lazy(() => import('../pages/campaign/campaignPage'))
const CampaignDetailPage = lazy(() => import('../pages/campaign-detail/CampaignDetailPage'))
const AppliedPage = lazy(() => import('../pages/applied/AppliedPage'))
const MediaPage = lazy(() => import('../pages/media/mediaPage'))

// Profile pages
const ProfilePage = lazy(() => import('../pages/profile/ProfilePage'))
const MyAccountPage = lazy(() => import('../pages/profile/MyAccountPage'))
const EditAccountPage = lazy(() => import('../pages/profile/EditAccountPage'))
const EditAddressPage = lazy(() => import('../pages/profile/EditAddressPage'))
const MyBankPage = lazy(() => import('../pages/profile/MyBankPage'))
const EditBankPage = lazy(() => import('../pages/profile/EditBankPage'))
const EditPasswordPage = lazy(() => import('../pages/profile/EditPasswordPage'))

// Auth pages
const SigninPage = lazy(() => import('../pages/auth/SigninPage'))
const SignupPage = lazy(() => import('../pages/auth/SignupPage'))
const SignupCompletePage = lazy(() => import('../pages/auth/SignupCompletePage'))
const LineValidationPage = lazy(() => import('../pages/auth/LineValidationPage'))
const FindEmailPage = lazy(() => import('../pages/auth/FindEmailPage'))
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'))
const ResetPasswordLineAuthPage = lazy(() => import('../pages/auth/ResetPasswordLineAuthPage'))
const ResetPasswordLineQRPage = lazy(() => import('../pages/auth/ResetPasswordLineQRPage'))
const ResetPasswordFormPage = lazy(() => import('../pages/auth/ResetPasswordFormPage'))

// Test pages
const ModalTestPage = lazy(() => import('../pages/test/ModalTestPage'))

// Route authentication configuration
export interface RouteAuth {
  requiresAuth?: boolean // 로그인 필요
  publicOnly?: boolean // 비로그인 전용 (로그인/회원가입)
  requiresLineId?: boolean // 라인 연동 필요
}

// Header configuration
export interface HeaderConfig {
  variant?: 'default' | 'back' | 'campaign' | 'none' | 'text'
  title?: string
  sticky?: boolean
  border?: boolean
  showSearch?: boolean
  backTo?: string // 뒤로가기 시 이동할 경로 (variant: 'back'일 때만 사용)
}

// Route definition interface
export interface RouteDefinition {
  path: string
  component: ComponentType
  auth?: RouteAuth
  header?: HeaderConfig
  seo?: SEOConfig // SEO 메타데이터
  todo?: boolean
  deprecated?: boolean
}

// Central route definitions - single source of truth
export const routeDefinitions = {
  // Main pages
  home: {
    path: '/',
    component: HomePage,
    auth: {
      // 기본적으로 로그인 시 라인 연동 체크가 AuthGuard에서 이루어짐
    },
    header: {
      variant: 'default',
      showSearch: true,
    },
    seo: {
      title: 'COCO｜日本最大級のKビューティーモニターサイト',
      description:
        '毎日さまざまなKビューティーモニターキャンペーンを公開中。今、韓国と日本で話題の人気アイテムを実際に体験してみましょう。',
      noindex: false, // 홈페이지만 인덱싱 허용
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        name: 'COCO - 主要ページ',
        description: 'COCOのキャンペーン一覧',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'HOME',
            description:
              '毎日さまざまなKビューティーモニターキャンペーンを公開中。今、韓国と日本で話題の人気アイテムを実際に体験してみましょう。',
            item: 'https://cococosme.net/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'モニターキャンペーン',
            description:
              'SNSで活動しているインフルエンサーの方は、人気のKビューティー商品を体験し、ご自身のチャンネルで紹介してみましょう。',
            item: 'https://cococosme.net/campaign?type=MONITOR',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'レビューキャンペーン',
            description: 'レビューを投稿して、今話題の人気アイテムを体験してみましょう。',
            item: 'https://cococosme.net/campaign?type=REVIEW',
          },
          {
            '@type': 'ListItem',
            position: 4,
            name: '購入キャンペーン',
            description: '指定された時間に商品を購入して、注目のアイテムを体験してみましょう。',
            item: 'https://cococosme.net/campaign?type=PURCHASE',
          },
        ],
      },
    },
  },
  // Campaign
  campaign: {
    path: '/campaign',
    component: CampaignPage,
    auth: {},
    header: {
      variant: 'campaign',
      showSearch: true,
    },
    seo: {
      noindex: false, // 캠페인 페이지 인덱싱 허용 (페이지 내에서 동적 SEO 설정)
    },
  },
  'campaign.detail': {
    path: '/campaign/:id',
    component: CampaignDetailPage,
    auth: {},
    header: {
      variant: 'back',
      title: 'campaign detail',
    },
    seo: {
      noindex: true, // 캠페인 상세 인덱싱 차단
    },
  },

  // Applied & Media
  applied: {
    path: '/applied',
    component: AppliedPage,
    auth: {},
    header: {
      variant: 'back',
      title: 'キャンペーンに申し込む',
    },
    seo: {
      noindex: true, // 신청 페이지 인덱싱 차단
    },
  },
  media: {
    path: '/media',
    component: MediaPage,
    auth: {},
    header: {
      variant: 'default',
    },
    seo: {
      noindex: true, // 미디어 페이지 인덱싱 차단
    },
  },

  // Profile routes - 명시적으로 requiresAuth와 requiresLineId 필요
  profile: {
    path: '/profile',
    component: ProfilePage,
    auth: {
      requiresAuth: true,
      requiresLineId: true,
    },
    header: {
      variant: 'text',
      title: 'マイページ',
    },
    seo: { noindex: true },
  },
  'profile.myAccount': {
    path: '/profile/my-account',
    component: MyAccountPage,
    auth: {
      requiresAuth: true,
      requiresLineId: true,
    },
    header: {
      variant: 'back',
      title: '個人情報の編集',
      backTo: '/profile',
    },
    seo: { noindex: true },
  },
  'profile.myAccount.editAccount': {
    path: '/profile/my-account/edit-account',
    component: EditAccountPage,
    auth: {
      requiresAuth: true,
      requiresLineId: true,
    },
    header: {
      variant: 'back',
      title: 'アカウント情報の編集',
      backTo: '/profile/my-account',
    },
    seo: { noindex: true },
  },
  'profile.myAccount.editAddress': {
    path: '/profile/my-account/edit-address',
    component: EditAddressPage,
    auth: {
      requiresAuth: true,
      requiresLineId: true,
    },
    header: {
      variant: 'back',
      title: 'お届け先情報の編集',
      backTo: '/profile/my-account',
    },
    seo: { noindex: true },
  },
  'profile.myBank': {
    path: '/profile/my-bank',
    component: MyBankPage,
    auth: {
      requiresAuth: true,
      requiresLineId: true,
    },
    header: {
      variant: 'back',
      title: '振込先口座の管理',
      backTo: '/profile',
    },
    seo: { noindex: true },
  },
  'profile.editBank': {
    path: '/profile/edit-bank',
    component: EditBankPage,
    auth: {
      requiresAuth: true,
      requiresLineId: true,
    },
    header: {
      variant: 'back',
      title: '振込先口座の管理',
      backTo: '/profile/my-bank',
    },
    seo: { noindex: true },
  },
  'profile.editPassword': {
    path: '/profile/edit-password',
    component: EditPasswordPage,
    auth: {
      requiresAuth: true,
      requiresLineId: true,
    },
    header: {
      variant: 'back',
      title: 'パスワードの変更',
      backTo: '/profile',
    },
    seo: { noindex: true },
  },

  // Auth routes
  'auth.signin': {
    path: '/auth/signin',
    component: SigninPage,
    auth: {
      publicOnly: true,
    },
    header: {
      variant: 'none',
    },
    seo: { noindex: true },
  },
  'auth.signup': {
    path: '/auth/signup',
    component: SignupPage,
    auth: {
      publicOnly: true,
    },
    header: {
      variant: 'none',
    },
    seo: { noindex: true },
  },
  'auth.signupComplete': {
    path: '/auth/signup-complete',
    component: SignupCompletePage,
    auth: {
      requiresAuth: true,
      requiresLineId: true,
    },
    header: {
      variant: 'none',
    },
    seo: { noindex: true },
  },
  'auth.lineValidation': {
    path: '/auth/line-validation',
    component: LineValidationPage,
    auth: {
      requiresAuth: true,
      // requiresLineId는 false - 라인 연동하러 가는 페이지이므로
    },
    header: {
      variant: 'text',
      title: '会員登録(2/2)',
    },
    seo: { noindex: true },
  },
  'auth.findEmail': {
    path: '/auth/find-email',
    component: FindEmailPage,
    auth: {
      publicOnly: true,
    },
    header: {
      variant: 'none',
    },
    seo: { noindex: true },
  },
  'auth.resetPassword': {
    path: '/auth/reset-password',
    component: ResetPasswordPage,
    auth: {
      publicOnly: true,
    },
    header: {
      variant: 'none',
    },
    seo: { noindex: true },
  },
  'auth.resetPasswordLineAuth': {
    path: '/auth/reset-password/line-auth',
    component: ResetPasswordLineAuthPage,
    auth: {
      publicOnly: true,
    },
    header: {
      variant: 'none',
    },
    seo: { noindex: true },
  },
  'auth.resetPasswordLineQR': {
    path: '/auth/reset-password/line-qr',
    component: ResetPasswordLineQRPage,
    auth: {
      publicOnly: true,
    },
    header: {
      variant: 'none',
    },
    seo: { noindex: true },
  },
  'auth.resetPasswordForm': {
    path: '/auth/reset-password/form',
    component: ResetPasswordFormPage,
    auth: {
      publicOnly: true,
    },
    header: {
      variant: 'none',
    },
    seo: { noindex: true },
  },

  // Test pages
  'test.modal': {
    path: '/test/modal',
    component: ModalTestPage,
    auth: {},
    seo: { noindex: true },
    header: {
      variant: 'none',
    },
  },
} as const

// Type exports
export type RouteName = keyof typeof routeDefinitions
export type RouteConfig = (typeof routeDefinitions)[RouteName]

// Extract route paths for type safety
export type RoutePath = RouteConfig['path']
